import { Link, useParams, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { BackupLogEntry, BackupRun, BackupRunObject } from '../api/types'
import { Diagnostica, EsitoBadge, formatSecondi, formatVelocita, fornitore, velocita } from '../components/backup'
import { formatBytes } from '../components/charts'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime } from '../components/common'
import { DataTable } from '../components/DataTable'
import { useVivo } from '../hooks/useAsync'
import { NotaCache } from '../components/NotaCache'
import { t } from '../i18n/lingua'

/** Una grandezza: in byte se il prodotto dà un numero, altrimenti il suo testo. */
function misura(bytes: number | null | undefined, testo?: string | null): string {
  if (bytes != null) return formatBytes(bytes)
  return testo || '—'
}

function durata(r: BackupRun): string {
  return r.duration_s != null ? formatSecondi(r.duration_s) : r.duration_text || '—'
}

const PROBLEMA = new Set(['failed', 'warning'])

/**
 * La velocità delle esecuzioni nel tempo: una barra per esecuzione, dalla più
 * vecchia alla più recente, altezza = GB/h. Una sola serie, quindi una tinta
 * sola e nessuna legenda; le esecuzioni con problemi hanno un segno «!» sopra
 * la barra (l'esito non è affidato al colore). Il tooltip di ogni barra dice
 * data, velocità ed esito; la tabella sotto è la vista completa.
 */
function GraficoVelocita({ runs }: { runs: BackupRun[] }) {
  const punti = [...runs]
    .reverse()
    .map((r) => ({ r, v: velocita(r) }))
    .filter((p): p is { r: BackupRun; v: number } => p.v != null)
  if (punti.length < 2) {
    return (
      <p className="muted">
        {t('Il grafico compare con almeno due esecuzioni di cui il prodotto dà dati elaborati e durata.')}
      </p>
    )
  }
  const max = Math.max(...punti.map((p) => p.v), 1)
  return (
    <figure className="colonne" aria-label={t('Velocità per esecuzione (GB/h)')}>
      <div className="colonne__area">
        {punti.map(({ r, v }) => {
          const problema = PROBLEMA.has((r.result ?? '').toLowerCase())
          const titolo = `${formatDateTime(r.start)} · ${formatVelocita(v)} · ${r.result ?? '—'}`
          return (
            <div className="colonne__col" key={r.id} title={titolo}>
              <span className="colonne__segno" aria-hidden={!problema}>
                {problema ? '!' : ''}
              </span>
              <span className="colonne__barra" style={{ height: `${Math.max((v / max) * 100, 2)}%` }} />
            </div>
          )
        })}
      </div>
      <figcaption className="colonne__asse">
        <span>{formatDateTime(punti[0].r.start)}</span>
        <span>{t('max {v}', { v: formatVelocita(max) })}</span>
        <span>{formatDateTime(punti[punti.length - 1].r.start)}</span>
      </figcaption>
    </figure>
  )
}

/** The level of a log line as the products write it, on the outcome badges. */
const LIVELLI: Record<string, string> = {
  success: 'success', succeeded: 'success', successful: 'success', ok: 'success',
  warning: 'warning', warn: 'warning',
  error: 'failed', failed: 'failed', fail: 'failed', fatal: 'failed', critical: 'failed',
}

function LivelloLog({ level }: { level: string | null }) {
  const noto = LIVELLI[(level ?? '').trim().toLowerCase()]
  return noto ? <EsitoBadge result={noto} /> : <span className="muted">{level || '—'}</span>
}

/** A log line with the parts the product highlights (objects, paths, codes) in bold. */
function TestoLog({ voce }: { voce: BackupLogEntry }) {
  const testo = voce.text ?? ''
  const evidenze = (voce.highlights ?? []).filter(Boolean)
  if (!evidenze.length) return <span className="backup-log__testo">{testo}</span>
  const escape = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parti = testo.split(new RegExp(`(${evidenze.map(escape).join('|')})`))
  return (
    <span className="backup-log__testo">
      {parti.map((p, i) => (evidenze.includes(p) ? <strong key={i}>{p}</strong> : p))}
    </span>
  )
}

/** Il log di un'esecuzione: si chiede solo quando la riga si apre. */
function LogEsecuzione({ vendor, run }: { vendor: string; run: BackupRun }) {
  const log = useVivo((fresco) => api.backupRunLog(vendor, run.id, fresco), [vendor, run.id])
  const d = log.data
  if (log.loading && !d) return <Spinner label={t('Leggo il log…')} />
  if (log.error) return <ErrorBanner error={log.error} />
  if (!d) return null
  if (!d.available) return <p className="muted">{d.reason ?? t('Log non disponibile.')}</p>
  const voci = d.entries ?? []
  const conOra = voci.some((v) => v.time)
  return (
    <>
      <div className="backup-log__barra">
        <NotaCache dati={d} />
        <button className="secondary" onClick={() => log.reload()} disabled={log.loading}>
          {log.loading ? t('Aggiorno…') : t('Aggiorna')}
        </button>
      </div>
      {!voci.length ? (
        <p className="muted">{t('Il prodotto non ha righe di log per questa esecuzione.')}</p>
      ) : (
        <DataTable
          rows={voci}
          rowKey={(_, i) => String(i)}
          columns={[
            ...(conOra
              ? [{
                  key: 'time', header: t('Ora'), value: (v: BackupLogEntry) => v.time ?? '',
                  render: (v: BackupLogEntry) => formatDateTime(v.time),
                }]
              : []),
            {
              key: 'level', header: t('Livello'), value: (v) => v.level ?? '',
              render: (v) => <LivelloLog level={v.level} />,
            },
            { key: 'text', header: t('Messaggio'), value: (v) => v.text ?? '', render: (v) => <TestoLog voce={v} /> },
          ]}
        />
      )}
      <Diagnostica voci={d.diagnostics} />
    </>
  )
}

function DettaglioEsecuzione({ vendor, run }: { vendor: string; run: BackupRun }) {
  return (
    <div className="stack">
      {run.message && <p className="banner banner--warn">{run.message}</p>}
      {run.objects && run.objects.length > 0 && (
        <>
          <h3 className="guida__titolo">{t('Oggetti dell’esecuzione')}</h3>
          <DataTable
            rows={run.objects}
            initialSort={{ key: 'name' }}
            rowKey={(o, i) => `${o.name ?? ''}-${i}`}
            columns={[
              { key: 'name', header: t('Oggetto'), value: (o: BackupRunObject) => o.name ?? '' },
              {
                key: 'result', header: t('Esito'), value: (o) => o.result ?? '',
                render: (o) => <EsitoBadge result={o.result} />,
              },
              { key: 'duration_s', header: t('Durata'), value: (o) => o.duration_s ?? 0, render: (o) => formatSecondi(o.duration_s) },
              {
                key: 'processed_bytes', header: t('Elaborati'), value: (o) => o.processed_bytes ?? 0,
                render: (o) => misura(o.processed_bytes),
              },
              {
                key: 'transferred_bytes', header: t('Trasferiti'), value: (o) => o.transferred_bytes ?? 0,
                render: (o) => misura(o.transferred_bytes),
              },
              {
                key: 'speed', header: t('Velocità'), value: (o) => velocita(o) ?? 0,
                render: (o) => formatVelocita(velocita(o)),
              },
              { key: 'bottleneck', header: t('Collo di bottiglia'), value: (o) => o.bottleneck ?? '' },
            ]}
          />
        </>
      )}
      <h3 className="guida__titolo">{t('Log')}</h3>
      <LogEsecuzione vendor={vendor} run={run} />
    </div>
  )
}

/**
 * La pagina di un job di backup, di qualunque fornitore: le ultime esecuzioni
 * con esito, durata, dati elaborati e trasferiti, velocità in GB/h e il loro
 * andamento; aprendo una riga, gli oggetti dell'esecuzione (Veeam) e il log.
 * Dati letti al momento attraverso la sonda, non salvati.
 */
export default function BackupJob() {
  const { vendor = '', id = '' } = useParams()
  const [query] = useSearchParams()
  const nome = query.get('name')
  const runs = useVivo((fresco) => api.backupJobRuns(vendor, id, nome, fresco), [vendor, id, nome])
  const d = runs.data
  const righe = d?.runs ?? []

  const concluse = righe.filter((r) => r.result)
  const riuscite = concluse.filter((r) => (r.result ?? '').toLowerCase() === 'success').length
  const durate = righe.map((r) => r.duration_s).filter((s): s is number => s != null)
  const velocitaNote = righe.map(velocita).filter((v): v is number => v != null)
  const media = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
  const ultima = righe[0]

  return (
    <>
      <header className="page__header">
        <div>
          <p className="page__subtitle">
            <Link to="/backup">{t('Backup')}</Link> · {fornitore(vendor)}
          </p>
          <h1 className="page__title">{nome || id}</h1>
        </div>
      </header>

      <Card
        title={t('Esecuzioni')}
        actions={
          <button className="secondary" onClick={() => runs.reload()} disabled={runs.loading}>
            {runs.loading ? t('Aggiorno…') : t('Aggiorna')}
          </button>
        }
      >
        <ErrorBanner error={runs.error} />
        <NotaCache dati={d} />
        {runs.loading && !d ? (
          <Spinner label={t('Interrogo il backup…')} />
        ) : !d ? null : !d.available ? (
          <p className="muted">{d.reason ?? t('Backup non interrogabile.')}</p>
        ) : !righe.length ? (
          <EmptyState
            title={t('Nessuna esecuzione')}
            hint={t('Il prodotto non ha esecuzioni recenti di questo job, oppure l’utenza non le vede.')}
          />
        ) : (
          <>
            <div className="stats">
              <StatCard label={t('Esecuzioni')} value={righe.length} />
              <StatCard
                label={t('Riuscite')}
                value={concluse.length ? `${Math.round((riuscite / concluse.length) * 100)}%` : '—'}
                tone={concluse.length && riuscite < concluse.length ? 'warn' : 'ok'}
                hint={t('{n} su {tot}', { n: riuscite, tot: concluse.length })}
              />
              <StatCard label={t('Durata media')} value={formatSecondi(media(durate) == null ? null : Math.round(media(durate) as number))} />
              <StatCard label={t('Velocità media')} value={formatVelocita(media(velocitaNote))} />
              <StatCard
                label={t('Dati dell’ultima esecuzione')}
                value={ultima ? misura(ultima.processed_bytes, ultima.processed_text) : '—'}
              />
            </div>
            <h3 className="guida__titolo">{t('Velocità per esecuzione (GB/h)')}</h3>
            <GraficoVelocita runs={righe} />
            <DataTable
              rows={righe}
              initialSort={{ key: 'start', dir: 'desc' }}
              rowKey={(r) => r.id}
              expand={(r) => <DettaglioEsecuzione vendor={vendor} run={r} />}
              columns={[
                { key: 'start', header: t('Inizio'), value: (r: BackupRun) => r.start ?? '', render: (r) => formatDateTime(r.start) },
                {
                  key: 'result', header: t('Esito'), value: (r) => r.result ?? '',
                  render: (r) => <EsitoBadge result={r.result} />,
                },
                { key: 'duration_s', header: t('Durata'), value: (r) => r.duration_s ?? 0, render: durata },
                {
                  key: 'processed_bytes', header: t('Elaborati'), value: (r) => r.processed_bytes ?? r.processed_text ?? '',
                  render: (r) => misura(r.processed_bytes, r.processed_text),
                },
                {
                  key: 'transferred_bytes', header: t('Trasferiti'), value: (r) => r.transferred_bytes ?? r.transferred_text ?? '',
                  render: (r) => misura(r.transferred_bytes, r.transferred_text),
                },
                {
                  key: 'speed', header: t('Velocità'), value: (r) => velocita(r) ?? 0,
                  render: (r) => formatVelocita(velocita(r)),
                },
                { key: 'bottleneck', header: t('Collo di bottiglia'), value: (r) => r.bottleneck ?? '' },
                { key: 'message', header: t('Messaggio'), value: (r) => r.message ?? '' },
              ]}
            />
          </>
        )}
      </Card>
    </>
  )
}
